/**
 * Vehicle requirement calculation utilities for fixed routes
 * 
 * These functions calculate the minimum number of vehicles needed
 * to operate fixed routes based on route timing, loop duration, and operating hours.
 */

import type { FixedRoute } from '../../types/fixedRoutes';

/**
 * Calculate the loop duration for a route (in minutes)
 * Reuses logic from timetableUtils.ts calculateLoopDuration()
 * 
 * @param route - The fixed route to calculate loop duration for
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Loop duration in minutes
 */
export function calculateRouteLoopDuration(
  route: FixedRoute,
  trafficMultiplier: number
): number {
  let total = 0;

  // Sum all segment travel times (with traffic multiplier)
  route.segments.forEach((seg) => {
    total += Math.round(seg.travelTimeMinutes * trafficMultiplier);
  });

  // Sum all stop wait times
  route.stops.forEach((stop) => {
    total += stop.waitTimeMinutes;
  });

  // For circulators: add return travel time from last stop to first stop
  if (route.isCirculator && route.stops.length > 1) {
    const lastStopId = route.stops[route.stops.length - 1].stopId;
    const firstStopId = route.stops[0].stopId;
    const returnSegment = route.segments.find(
      (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
    );

    if (returnSegment) {
      total += Math.round(returnSegment.travelTimeMinutes * trafficMultiplier);
    } else if (route.segments.length > 0) {
      // Fallback: use first segment as return travel time
      total += Math.round(route.segments[0].travelTimeMinutes * trafficMultiplier);
    }
  }

  return total;
}

/**
 * Calculate the operating duration for a route (in minutes)
 * 
 * @param route - The fixed route
 * @returns Operating duration in minutes
 */
function calculateOperatingDuration(route: FixedRoute): number {
  const startMinutes = timeToMinutes(route.startTime);
  const endMinutes = route.endTime 
    ? timeToMinutes(route.endTime)
    : 22 * 60; // Default to 22:00 if not specified
  
  // Handle overnight shifts (end < start)
  if (endMinutes < startMinutes) {
    return (24 * 60) - startMinutes + endMinutes;
  }
  
  return endMinutes - startMinutes;
}

/**
 * Convert HH:mm time string to total minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Calculate minimum vehicles needed for a circulator route
 * 
 * For a circulator route that runs continuously from start time to end time:
 * - One vehicle can complete multiple loops sequentially
 * - If the loop duration is shorter than the operating duration, one vehicle
 *   can handle all loops by running continuously throughout the period
 * - The absolute minimum is 1 vehicle if loopDuration <= operatingDuration
 * 
 * Formula: Always return 1 vehicle minimum, since one vehicle can complete
 * all loops sequentially during the operating period.
 * 
 * Note: This calculates the absolute minimum. If you need to maintain a specific
 * headway/frequency (e.g., a vehicle every 15 minutes), you would need additional
 * vehicles calculated as: Math.ceil(loopDuration / desiredHeadwayMinutes)
 * 
 * @param route - The circulator route
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Minimum number of vehicles needed (always 1 for continuous operation)
 */
export function calculateMinimumVehiclesForCirculator(
  route: FixedRoute,
  trafficMultiplier: number
): number {
  const loopDuration = calculateRouteLoopDuration(route, trafficMultiplier);
  const operatingDuration = calculateOperatingDuration(route);

  if (loopDuration <= 0) {
    return 1; // Invalid loop duration, default to 1
  }

  if (operatingDuration <= 0) {
    return 0; // Invalid operating duration (route has no operating time)
  }

  // For circulators running continuously from start to end:
  // One vehicle can complete all loops sequentially during the operating period
  // Example: If loop takes 30 min and route runs 8:00-22:00 (14 hours = 840 min),
  // one vehicle can complete 840/30 = 28 loops sequentially
  // Therefore, minimum vehicles = 1
  
  // However, if loopDuration > operatingDuration (edge case where one loop
  // is longer than the entire operating period), we'd need at least 1 vehicle anyway
  return 1;
}

/**
 * Calculate minimum vehicles needed for a non-circulator route
 * 
 * Non-circulator routes run once per operating period, so minimum vehicles = 1
 * 
 * @param route - The non-circulator route
 * @returns Minimum number of vehicles needed (always 1)
 */
export function calculateMinimumVehiclesForNonCirculator(
  route: FixedRoute
): number {
  return 1;
}

/**
 * Calculate minimum vehicles needed for a route
 * Routes to the appropriate calculation function based on route type
 * 
 * @param route - The fixed route
 * @param trafficMultiplier - Traffic multiplier to apply to segment travel times
 * @returns Minimum number of vehicles needed
 */
export function calculateMinimumVehicles(
  route: FixedRoute,
  trafficMultiplier: number
): number {
  if (route.isCirculator) {
    return calculateMinimumVehiclesForCirculator(route, trafficMultiplier);
  } else {
    return calculateMinimumVehiclesForNonCirculator(route);
  }
}

